import { useState, FormEvent } from 'react'
import { tenantConfig } from '../../config/tenant'
import { api } from '../../api/client'
import Nav from '../../components/Nav'
import Seo from '../../components/Seo'
import Footer from '../../components/Footer'
import { BROKERS } from '../../data/team'

/**
 * ContactPage — Greystone marketing "Contact" route.
 *
 * Ported from docs/SaaS/greystone-mockups-light/contact.html with the §4.1
 * simplification cuts applied (GreyStoneSaasExecutionPlan.md line 187):
 *   KEEP  — hero + lead form + 4 broker cards with Calendly.
 *   DROP  — the map placeholder, the contact-details sidebar, and the
 *           Sophie/Daniel secondary chips (never real staff, D51).
 *
 * The mockup's "What can we help with?" loan-type select is replaced by the
 * confirmed "How did you find us?" attribution field (B3). The form POSTs to
 * /api/leads with source "contact" (execution plan line 141) and swaps to a
 * thank-you state on success.
 *
 * Each broker's "Book a call" links to their Calendly (src/data/calendly.ts).
 * Brett Richardson has no headshot — his card uses a
 * "BR" initials avatar (never fabricate a photo).
 */

const FIND_US_OPTIONS = [
  'Google',
  'Referral',
  'Social media',
  'Existing client',
  'Event',
  'Advertisement',
  'Other',
]

interface LeadResponse {
  id: string
  status: string
}

export default function ContactPage() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [message, setMessage] = useState('')
  const [findUs, setFindUs] = useState('')

  const [emailError, setEmailError] = useState<string | null>(null)
  const [serverError, setServerError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const canSubmit =
    !!name.trim() && !!email.trim() && !!phone.trim() && !emailError && !submitting

  function validateEmail(v: string) {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      setEmailError('Enter a valid email address.')
    } else setEmailError(null)
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setServerError(null)
    setSubmitting(true)
    // Fold the free-text message and the attribution answer into notes so the
    // broker sees both in the lead email (backend lead has no dedicated field).
    const noteParts: string[] = []
    if (message.trim()) noteParts.push(message.trim())
    if (findUs) noteParts.push(`How did you find us? ${findUs}`)
    try {
      await api.post<LeadResponse>('/api/leads', {
        customer_name: name.trim(),
        customer_email: email.trim(),
        customer_phone: phone.trim(),
        notes: noteParts.length ? noteParts.join('\n') : null,
        source: 'contact',
        loan_details: {},
      })
      setSubmitted(true)
    } catch (err: unknown) {
      setServerError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Seo
        title="Contact Us"
        path="/contact"
        description="Talk to a mortgage broker, book a call or send an enquiry and we'll be in touch within one business day."
      />
      <Nav />
      <main id="main" className="pt-36 pb-20">
        {/* HERO */}
        <section className="max-w-5xl mx-auto px-6 pb-12 text-center">
          <div className="text-xs font-semibold uppercase tracking-wider text-gs-text-muted mb-3">
            Contact us
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-ink font-semibold leading-tight mb-4">
            Let&apos;s chat about your goals.
          </h1>
          <p className="text-lg text-gs-text-soft max-w-2xl mx-auto">
            Send us a message and one of our brokers will be in touch within one business day,
            or book a call directly with the person who fits your situation.
          </p>
        </section>

        {/* FORM */}
        <section className="max-w-2xl mx-auto px-6 pb-20">
          <div className="rounded-3xl border border-[#E8E8E2] bg-white p-8 md:p-10 shadow-card">
            {submitted ? (
              <div className="text-center py-6" data-testid="contact-success">
                <h2 className="font-heading text-2xl md:text-3xl text-ink mb-2">Thank you</h2>
                <p className="text-gs-text-soft">
                  Your message is on its way. A {tenantConfig.name} broker will be in touch within
                  one business day.
                </p>
              </div>
            ) : (
              <>
                <h2 className="font-heading text-2xl md:text-3xl text-ink mb-2">Send us a message</h2>
                <p className="text-gs-text-soft mb-8">We&apos;ll be in touch within one business day.</p>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-ink mb-1.5">
                      Full name
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      autoComplete="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-4 py-3 border border-[#E8E8E2] rounded-xl text-sm focus:border-ink transition-colors"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-ink mb-1.5">
                      Email
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onBlur={(e) => validateEmail(e.target.value)}
                      aria-invalid={!!emailError}
                      className="w-full px-4 py-3 border border-[#E8E8E2] rounded-xl text-sm focus:border-ink transition-colors"
                      required
                    />
                    {emailError && <div className="text-red-600 text-xs mt-1">{emailError}</div>}
                  </div>

                  <div>
                    <label htmlFor="contact-phone" className="block text-sm font-medium text-ink mb-1.5">
                      Phone
                    </label>
                    <input
                      id="contact-phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full px-4 py-3 border border-[#E8E8E2] rounded-xl text-sm focus:border-ink transition-colors"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-find-us" className="block text-sm font-medium text-ink mb-1.5">
                      How did you find us?
                    </label>
                    <select
                      id="contact-find-us"
                      value={findUs}
                      onChange={(e) => setFindUs(e.target.value)}
                      className="w-full px-4 py-3 border border-[#E8E8E2] rounded-xl text-sm bg-white text-ink focus:border-ink transition-colors"
                    >
                      <option value="">Please select…</option>
                      {FIND_US_OPTIONS.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-ink mb-1.5">
                      Message
                    </label>
                    <textarea
                      id="contact-message"
                      rows={4}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="What are you looking to achieve?"
                      className="w-full px-4 py-3 border border-[#E8E8E2] rounded-xl text-sm resize-none focus:border-ink transition-colors"
                    />
                  </div>

                  {serverError && <div className="text-red-600 text-sm">{serverError}</div>}

                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className="w-full py-3.5 rounded-full font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
                  >
                    {submitting ? 'Sending…' : 'Send Message'}
                  </button>

                  <p className="text-xs text-gs-text-muted text-center">
                    By submitting, you agree to our privacy policy. We&apos;ll never share your details.
                  </p>
                </form>
              </>
            )}
          </div>
        </section>

        {/* BOOK A BROKER */}
        <section className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs font-semibold uppercase tracking-wider text-gs-text-muted mb-2">
              Speak to a broker
            </div>
            <h2 className="font-heading text-3xl md:text-4xl text-ink mb-3">
              Find the right person for your situation.
            </h2>
            <p className="text-gs-text-soft">
              Each of our brokers brings different expertise, book directly with the one who fits.
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {BROKERS.filter((b) => b.bookable !== false).map((b) => (
              <div
                key={b.name}
                className="rounded-2xl border border-[#E8E8E2] bg-white p-6 text-center card-hover"
              >
                {b.image ? (
                  <img
                    src={b.image}
                    alt={b.name}
                    className="w-36 h-36 rounded-2xl object-cover mx-auto mb-5"
                  />
                ) : (
                  <div
                    className="w-36 h-36 rounded-2xl flex items-center justify-center mx-auto mb-5"
                    style={{ background: tenantConfig.accentColor }}
                    aria-hidden="true"
                  >
                    <span className="font-heading font-bold text-2xl text-ink">{b.initials}</span>
                  </div>
                )}
                <h3 className="font-heading text-lg text-ink font-semibold">{b.name}</h3>
                <p className="text-gs-text-muted text-sm mt-1 mb-2">{b.title}</p>
                <p className="text-gs-text-soft text-xs mb-5">{b.focus}</p>
                <a
                  href={b.calendly ?? `mailto:${b.email}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 font-medium text-sm px-5 py-2 rounded-full transition-opacity hover:opacity-90"
                  style={{ background: tenantConfig.primaryColor, color: '#fff' }}
                >
                  Book a call
                  <span aria-hidden="true">→</span>
                </a>
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </>
  )
}
